import { useContext, useMemo } from 'react';

import { PanelsContext } from './PanelsContext';
import { PanelLayout, PanelMode, PanelRestoredState, PanelState } from './types';

type HookArgs = {
  panelPoppedOut?: boolean;
  uniqueId: string;
};

export const useRestoreStateFrom = ({ panelPoppedOut, uniqueId }: HookArgs) => {
  const { panelStateOptions } = useContext(PanelsContext);

  return useMemo((): PanelRestoredState | null | undefined => {
    if (!panelStateOptions) {
      return;
    }

    if (panelStateOptions.saveStateIn === 'external') {
      return panelStateOptions.onRestoreState?.();
    }

    const uniqueKey = createUniqueKey({
      saveStateKey: panelStateOptions.saveStateKey,
      uniqueId,
    });

    try {
      const storedState = localStorage.getItem(uniqueKey);
      if (storedState) {
        const { mode, ...state } = JSON.parse(storedState) as PanelState;

        const currentMode: PanelMode = panelPoppedOut === undefined ? mode : panelPoppedOut ? 'poppedOut' : 'poppedIn';
        const panelLayout = state[currentMode];

        return {
          panelPoppedOut: currentMode === 'poppedOut',
          ...panelLayout,
        };
      }
    } catch (e) {
      console.error('Failed to read stored panel state!', e);
    }

    return null;
  }, [panelPoppedOut, panelStateOptions, uniqueId]);
};

export const useSaveStateIn = ({ panelPoppedOut = false, uniqueId }: HookArgs) => {
  const { panelStateOptions } = useContext(PanelsContext);
  return (panelLayout: PanelLayout) => {
    if (!panelStateOptions) {
      return;
    }

    const uniqueKey = createUniqueKey({
      saveStateKey: panelStateOptions.saveStateKey,
      uniqueId,
    });

    const mode: PanelMode = panelPoppedOut ? 'poppedOut' : 'poppedIn';

    if (panelStateOptions.saveStateIn === 'external') {
      panelStateOptions.onSaveState?.({
        mode,
        [mode]: panelLayout,
      });
      return;
    }

    const storedState = localStorage.getItem(uniqueKey);

    let panelState: PanelState = {
      mode,
      [mode]: panelLayout,
    };

    if (storedState) {
      try {
        panelState = JSON.parse(storedState) as PanelState;

        panelState.mode = mode;
        panelState[mode] = {
          ...panelState[mode],
          ...panelLayout,
        };
      } catch (e) {
        console.error('Failed to read existing panel state!', e);
      }
    }

    try {
      localStorage.setItem(uniqueKey, JSON.stringify(panelState));
    } catch (e) {
      console.error('Failed to save panel state!', e);
    }
  };
};

function createUniqueKey({ saveStateKey, uniqueId }: { saveStateKey: string; uniqueId: string }): string {
  return `panel-${saveStateKey}-${uniqueId}`;
}
